import React, { useState } from "react";
import { Box } from "@mui/material";
import MapLevelSwitch from "./MapLevelSwitch";

interface MapTableWithLevelSwitchProps {
    stateMapComponent: React.ReactNode;
    countyMapComponent: React.ReactNode;
    stateContentComponent: React.ReactNode;
    countyTableComponent: React.ReactNode;
    countyDataLoading: boolean;
    onCountyDataRequest: () => void;
    hasCountyData: boolean;
    level?: "state" | "county";
    defaultLevel?: "state" | "county";
    onLevelChange?: (level: "state" | "county") => void;
    countyControlsComponent?: React.ReactNode;
    countyUnavailableComponent?: React.ReactNode;
    mapAreaWidth?: string;
}

const MapTableWithLevelSwitch = ({
    stateMapComponent,
    countyMapComponent,
    stateContentComponent,
    countyTableComponent,
    countyDataLoading,
    onCountyDataRequest,
    hasCountyData,
    level: controlledLevel,
    defaultLevel = "state",
    onLevelChange,
    countyControlsComponent,
    countyUnavailableComponent,
    mapAreaWidth = "85%"
}: MapTableWithLevelSwitchProps): JSX.Element => {
    const [internalLevel, setInternalLevel] = useState<"state" | "county">(defaultLevel);
    const level = controlledLevel ?? internalLevel;

    const handleLevelChange = (newLevel: "state" | "county") => {
        if (controlledLevel === undefined) {
            setInternalLevel(newLevel);
        }
        if (onLevelChange) {
            onLevelChange(newLevel);
        }
        if (newLevel === "county" && !hasCountyData) {
            onCountyDataRequest();
        }
    };

    React.useEffect(() => {
        if (level === "county" && !hasCountyData) {
            onCountyDataRequest();
        }
    }, [level, hasCountyData, onCountyDataRequest]);

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                className="mapArea"
                component="div"
                sx={{
                    width: mapAreaWidth,
                    m: "auto"
                }}
            >
                <Box sx={{ display: level !== "state" ? "none" : "block" }}>{stateMapComponent}</Box>
                <Box sx={{ display: level !== "county" ? "none" : "block" }}>
                    {hasCountyData && countyControlsComponent}
                    {hasCountyData && (countyUnavailableComponent || countyMapComponent)}
                </Box>
                <MapLevelSwitch level={level} onLevelChange={handleLevelChange} disabled={countyDataLoading} />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    height: 50
                }}
            />
            <Box sx={{ display: level !== "state" ? "none" : "block" }}>{stateContentComponent}</Box>
            <Box sx={{ display: level !== "county" ? "none" : "block" }}>
                <Box
                    className="chartArea"
                    component="div"
                    sx={{
                        width: "100%",
                        m: "auto"
                    }}
                >
                    {hasCountyData && !countyUnavailableComponent && countyTableComponent}
                </Box>
            </Box>
        </Box>
    );
};

export default MapTableWithLevelSwitch;
